import { useCallback, useState } from 'react';
import { pickImageDataUrls, prepareFilesForUpload } from '@/lib/file-uploads';

export interface AliceAttachment {
  id: string;
  dataUrl: string;
}

const MAX_ATTACHMENTS = 4;

/**
 * Manages the pending image attachments for a single Alice turn. Images come
 * from the file picker or the camera dialog, are capped at MAX_ATTACHMENTS, and
 * are cleared after the message is sent (per-turn vision only — nothing is
 * persisted or re-sent).
 */
export function useAliceAttachments() {
  const [attachments, setAttachments] = useState<AliceAttachment[]>([]);

  const addDataUrls = useCallback((dataUrls: string[]) => {
    if (dataUrls.length === 0) return;
    setAttachments((prev) => {
      const next = [...prev];
      for (const dataUrl of dataUrls) {
        if (next.length >= MAX_ATTACHMENTS) break;
        next.push({ id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, dataUrl });
      }
      return next;
    });
  }, []);

  const addFiles = useCallback(
    async (files: File[]) => {
      const images = files.filter((file) => file.type.startsWith('image/'));
      if (images.length === 0) return;
      const prepared = await prepareFilesForUpload(images);
      addDataUrls(pickImageDataUrls(prepared, MAX_ATTACHMENTS));
    },
    [addDataUrls],
  );

  const remove = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const clear = useCallback(() => setAttachments([]), []);

  return {
    attachments,
    addFiles,
    addDataUrl: (dataUrl: string) => addDataUrls([dataUrl]),
    remove,
    clear,
    full: attachments.length >= MAX_ATTACHMENTS,
  };
}
